import React from "react";
import {List, Map} from "immutable";
import {cx} from "../../lib/ClassSet";
import {splitDimensions} from "../../lib/Dimensions";
import {autoFormat as formatTelephoneNr} from "../../lib/TelephoneNormalizer";
import {formatPaymentMethod} from "../../lib/Money";
import {relativeDistanceUTCFromString} from "../../lib/DateFormatter";
import {formatFulfillmentStatus} from "../../lib/FulfillmentStatus";
import {hasStateProvince} from "../../lib/AddressRules";
import {Link} from "react-router";
import "./PropertiesList.css";
import StatusTag from "../StatusTag";
import {formatStatus} from "../../lib/TextFormatter";

function formatUTC(value) {
  if (value == null) return "";
  return String(value).substr(0, 19).split("T").join(" @ ");
}

function formatRelativeUTC(value) {
  if (value == null) return "";
  return relativeDistanceUTCFromString(value);
}

function formatBool(value) {
  if (value === true) return "Yes";
  return "No";
}

function renderDimsType(dims) {
  let [w, h, d] = splitDimensions(dims);
  return (
    <div className="t-dims">
      <span className="t-string">W: <span className="t-number">{w}</span></span>
      <span className="t-string">, H: <span className="t-number">{h}</span></span>
      <span className="t-string">, D: <span className="t-number">{d}</span></span>
    </div>
  )
}

function renderAddress(address = Map()) {
  if (address == null) return null;
  let classes = cx({
    "t-address": true,
    "u-addressLine2": address.get("addressAddressLine2", "").length> 0,
    "u-showStateProvince": hasStateProvince(address.get("addressCountryCode"))
  })
  return (
    <div className={classes}>
      {address.get("addressAddressLine1", "")}
      <span className="addressLine2">, {address.get("addressAddressLine2", "")}</span>
      , {address.get("addressCity", "")}
      <span className="addressStateProvince">, {address.get("addressStateProvince", "")}</span>
      , {address.get("addressPostcode", "")}
      , {address.get("addressCountryName", "")}
    </div>
  );
}

function renderTags(tags = Map()) {
  if (tags == null) return null;

  const classes = cx({
    "t-tags": true,
  });

  return (
    <div className={classes}>
      {tags.map(name => <RenderTag name={name} />)}
    </div>
  )
}
function RenderTag({name}) {
  return (
    <StatusTag
      color={StatusTag.COLORS.OUTLINE}
      text={name.substr(3)} // Strip hw: prefix
      title={name}
    />
  );
}

function renderShippingAddress(address = Map()) {
  if (address == null) return null;
  let classes = cx({
    "t-address": true,
    "u-addressLine2": address.get("addressAddressLine2", "").length> 0,
    "u-email": address.get("email", "").length > 0,
    "u-attName": address.get("attName", "").length > 0,
    "u-phoneNr": address.get("phoneNr", "").length > 0,
    "u-showStateProvince": hasStateProvince(address.get("addressCountryCode"))
  })
  return (
    <div className={classes}>
      <span className="type">{address.get("type", "")}</span>
      <span className="name">{address.get("name", "")}</span>
      <span className="att">Attn. {address.get("attName", "")}</span>
      <span className="phoneNr">Phone Nr. {formatTelephoneNr(address.get("phoneNr", ""))}</span>
      <span className="email">{address.get("email", "")}</span>    
      {address.get("addressAddressLine1", "")}
      <span className="addressLine2">, {address.get("addressAddressLine2", "")}</span>
      , {address.get("addressCity", "")}
      , <span className="addressStateProvince">{address.get("addressStateProvince", "")}</span>
      , {address.get("addressPostcode", "")}
      , {address.get("addressCountryName", "")}      
    </div>
  )
}

function renderType(type, prop, data, defaultValue, fn) {
  if (fn !== null) {
    let [newValue, newType] = fn(data);
    return renderType(newType, prop, data.set(prop, newValue), defaultValue, null);
  }
  let props = prop.split(".");

  if (data && data.get("status") === "deleted") {
    // Special case for deleted customers
    if (prop === "name") return <span className="t-string">Deleted Customer</span>
  }

  const maybeValue = data.getIn(props, defaultValue)
  
  switch(type) {
    case "id":
      return <span className="t-id">{maybeValue}</span>
    case "salesOrderId":
      return <MaybeLinkTo linkBase="/sales/orders/" maybeValue={maybeValue} />;
    case "shippingOrderId":
      return <MaybeLinkTo linkBase="/shipping/orders/" maybeValue={maybeValue} />
    case "externalOrderId":
      // For now only meta orders are supported
      const externalSource = data.get("externalSource", "")
      if (!maybeValue || !["facebook", "instagram"].includes(externalSource)) return maybeValue;

      return <Link to={`/sales/metaOrders/${maybeValue}`} className="t-id">{maybeValue}</Link>
    case "customerId":
      return <MaybeLinkTo linkBase="/customers/" maybeValue={maybeValue} />
    case "accountId":
      return <MaybeLinkTo linkBase="/accounts/" maybeValue={maybeValue} />
    case "subscriptionId":
      return <MaybeLinkTo linkBase="/subscriptions/" maybeValue={maybeValue} />
    case "explicitId":
      return <span className="t-id t-explicit">{maybeValue}</span>
    case "string":
      return <span className="t-string">{maybeValue}</span>
    case "status":
      // Like a string but we strip the underscore
      return <span className="t-status">{formatStatus(maybeValue)}</span>
    case "text":
      return <span className="t-string t-text">{maybeValue}</span>
    case "number":
      return <span className="t-number">{maybeValue}</span>
    case "array":
      return <span className="t-number">{data.getIn(props, []).join(", ")}</span>
    case "boolean":
      return <span className="t-string">{formatBool(maybeValue)}</span>
    case "paymentMethod":
      return <span className="t-string">{formatPaymentMethod(maybeValue)}</span>
    case "telephone":
      return <span className="t-string">{formatTelephoneNr(maybeValue)}</span>
    case "dims":
      return renderDimsType(maybeValue)
    case "address":
      return renderAddress(data.getIn(props, Map()))
    case "tags":
      return renderTags(data.getIn(props, Map()));
    case "shippingAddress":
      return renderShippingAddress(data.getIn(props, Map()))
    case "fulfillmentStatus":
      return <span className="t-string" title={maybeValue}>{formatFulfillmentStatus(maybeValue)}</span>
    case "utc":
      return <span className="t-utc">{formatUTC(maybeValue)}</span>
    case "relativeUTC":
      return <span className="t-relative-utc">{formatRelativeUTC(maybeValue)}</span>
    default:
      return String(data.getIn(props));
  }
}

function MaybeLinkTo({linkBase, maybeValue}) {
  if (!maybeValue) return maybeValue;

  return <Link to={`${linkBase}${maybeValue}`} className="t-id">{maybeValue}</Link>
}

function renderSuffix(suffix) {
  if (suffix === "") return null;
  return <span className="suffix">{suffix}</span>
}

function renderProperty(definition, data, allowPropertyClick, onPropertyClick) {
  let classes = cx({
    "help": definition.has("help")
  })
  let clickAction = () => {}
  if (allowPropertyClick) {
    clickAction = () => {
      onPropertyClick(definition, data);
    }
  }
  return (
    <tr key={definition.get("prop")} onClick={clickAction}>
      <th className={classes} title={definition.get("help", "")}><span>{definition.get("label")}</span></th>
      <td>
        {renderType(definition.get("type"), definition.get("prop"), data, definition.get("default", ""), definition.get("fn", null))}
        {renderSuffix(definition.get("suffix", ""))}
      </td>
    </tr>
  )
}

function renderCategory(definition) {
  return (
    <tr key={definition.get("prop")} className="prop-category">
      <th colSpan="2">{definition.get("label")}</th>
    </tr>
  )
}

const PropertiesList = (props) => {


  let elements = props.properties.map(x => {
    return x.get("type") === "category" ? renderCategory(x) : renderProperty(x, props.data, props.allowPropertyClick, props.onPropertyClick)
  })


  return (
    <table className="PropertiesList">
      <tbody>
        {elements}
      </tbody>
    </table>
  );



}

PropertiesList.defaultProps = {
  properties: List(),
  data: Map(),
  allowPropertyClick: false
}

export default PropertiesList;